import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { cloneElement, isValidElement, useId } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { Icon } from '../icons/icons';
import styles from './FormGroup.module.scss';
export interface FormGroupProps {
  /**
   * The label's content: text, or text with inline markup (a hint span, a
   * tooltip trigger, an `aria-hidden` node), rendered as is inside the
   * `<label>` (PR 5.1, O163).
   */
  label: ReactNode;
  htmlFor: string;
  /**
   * The error message: text, or text with inline markup such as a link,
   * rendered as is inside the `role="alert"` message (PR 5.1, O163).
   */
  error?: ReactNode;
  required?: boolean;
  children: ReactNode;
}
type ControlProps = {
  'aria-describedby'?: string;
  'aria-invalid'?: boolean | 'true' | 'false' | 'grammar' | 'spelling';
  'aria-required'?: boolean | 'true' | 'false';
};

/**
 * A label, one control and its error. With an `error`, the control gains
 * `aria-describedby` (the error's id, after any id it already names) and
 * `aria-invalid`, which is what the Helios controls draw their error state
 * from; the error is a `role="alert"` message in the surface's strong text
 * after a red cross. With `required`, the control gains `aria-required`
 * and the label a marker drawn as generated content that assistive
 * technology skips (the control carries the state). The control takes its
 * own `id` (the label's `htmlFor`), and native `required` if the form wants
 * the browser's validation.
 */
function FormGroup(t0) {
  const $ = _c(19);
  const {
    label,
    htmlFor,
    error,
    required: t1,
    children
  } = t0;
  const required = t1 === undefined ? false : t1;
  const errorId = useId();
  const hasError = error != null && error !== false && error !== "";
  let t2;
  if ($[0] !== children || $[1] !== errorId || $[2] !== hasError || $[3] !== required) {
    t2 = (hasError || required) && isValidElement(children) ? cloneElement(children as ReactElement<ControlProps>, {
      ...(hasError && {
        "aria-describedby": [(children as ReactElement<ControlProps>).props["aria-describedby"], errorId].filter(Boolean).join(" "),
        "aria-invalid": true
      }),
      ...(required && {
        "aria-required": true
      })
    }) : children;
    $[0] = children;
    $[1] = errorId;
    $[2] = hasError;
    $[3] = required;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const control = t2;
  let t3;
  if ($[5] !== required) {
    t3 = classNames(styles["helios-form-group__label"], {
      [styles["helios-form-group__label--required"]]: required
    });
    $[5] = required;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== htmlFor || $[8] !== label || $[9] !== t3) {
    t4 = <label htmlFor={htmlFor} className={t3}>{label}</label>;
    $[7] = htmlFor;
    $[8] = label;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== error || $[12] !== errorId || $[13] !== hasError) {
    t5 = hasError && <span id={errorId} className={styles["helios-form-group__error"]} role="alert"><Icon name="xmark" className={styles["helios-form-group__error-icon"]} /><span>{error}</span></span>;
    $[11] = error;
    $[12] = errorId;
    $[13] = hasError;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== control || $[16] !== t4 || $[17] !== t5) {
    t6 = <div data-helios-form-group="" className={styles["helios-form-group"]}>{t4}{control}{t5}</div>;
    $[15] = control;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
}
export default FormGroup;